import { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { ArrowLeft, Save, Upload, Trash2, Image, X } from "lucide-react";
import Navbar from "@/components/Navbar";
import { useProperty, useUpdateProperty } from "@/hooks/useProperties";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { usePropertyContext } from "@/contexts/PropertyContext";
import { canEditPropertyConfig } from "@/lib/propertyTabs";
import { AddressAutocomplete, type AddressDetails } from "@/components/owner/AddressAutocomplete";

function normalizePostal(s: string) {
  return s.replace(/\s+/g, " ").trim().toUpperCase();
}

/** True when street/city/state/postal still match a Places snapshot (unit may differ). */
function formMatchesPlacesLock(
  address: string,
  city: string,
  stateProvince: string,
  postalCode: string,
  lock: AddressDetails | null
): boolean {
  if (!lock) return false;
  return (
    address.trim() === (lock.street_address || "").trim() &&
    city.trim() === (lock.city || "").trim() &&
    stateProvince.trim() === (lock.state_province || "").trim() &&
    normalizePostal(postalCode) === normalizePostal(lock.postal_code || "")
  );
}

const EditProperty = () => {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const ctx = usePropertyContext();

  const { data: property, isLoading } = useProperty(propertyId);
  const updateProperty = useUpdateProperty();

  // When opened via /properties/:id/edit (unified route), redirect Limited team admin
  useEffect(() => {
    if (!propertyId || !ctx) return;
    if (!canEditPropertyConfig(ctx.role ?? "owner", ctx.accessLevel, ctx.permissions)) {
      navigate(`/properties/${propertyId}`, { replace: true });
    }
  }, [propertyId, ctx?.role, ctx?.accessLevel, ctx?.permissions, navigate]);

  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [unit, setUnit] = useState("");
  const [city, setCity] = useState("");
  const [stateProvince, setStateProvince] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [notes, setNotes] = useState("");
  const [hospitablePropertyId, setHospitablePropertyId] = useState("");
  const [photoUrls, setPhotoUrls] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [exitConfirmOpen, setExitConfirmOpen] = useState(false);
  const [pendingExitTarget, setPendingExitTarget] = useState<"back" | "cancel" | null>(null);
  /** Last Google Places snapshot tied to lat/lng; hydrated from property when coords exist. */
  const [placesLock, setPlacesLock] = useState<AddressDetails | null>(null);

  // Snapshot of initial values (after property loads) for unsaved detection
  const initialSnapshotRef = useRef<{ name: string; address: string; unit: string; city: string; stateProvince: string; postalCode: string; notes: string; hospitablePropertyId: string; photoUrls: string[] } | null>(null);

  // Populate form when property loads
  useEffect(() => {
    if (property) {
      const urls = (property as { property_photo_urls?: string[] | null }).property_photo_urls;
      const photos = urls?.length ? urls : (property as any).property_photo_url ? [(property as any).property_photo_url] : [];
      const hid = String((property as { hospitable_property_id?: string | null }).hospitable_property_id || "").trim();
      setName(property.name || "");
      setAddress(property.address || "");
      setUnit((property as any).unit || "");
      setCity((property as any).city || "");
      setStateProvince((property as any).state_province || "");
      setPostalCode((property as any).postal_code || "");
      setNotes((property as any).notes || "");
      setHospitablePropertyId(hid);
      setPhotoUrls(photos);
      const lat = (property as { latitude?: number | null }).latitude;
      const lng = (property as { longitude?: number | null }).longitude;
      if (
        lat != null &&
        lng != null &&
        Number.isFinite(lat) &&
        Number.isFinite(lng)
      ) {
        setPlacesLock({
          street_address: property.address || "",
          city: (property as { city?: string | null }).city || "",
          state_province: (property as { state_province?: string | null }).state_province || "",
          postal_code: (property as { postal_code?: string | null }).postal_code || "",
          country: (property as { country?: string | null }).country || "",
          formatted_address: (property as { formatted_address?: string | null }).formatted_address || "",
          latitude: lat,
          longitude: lng,
        });
      } else {
        setPlacesLock(null);
      }
      initialSnapshotRef.current = {
        name: property.name || "",
        address: property.address || "",
        unit: (property as any).unit || "",
        city: (property as any).city || "",
        stateProvince: (property as any).state_province || "",
        postalCode: (property as any).postal_code || "",
        notes: (property as any).notes || "",
        hospitablePropertyId: hid,
        photoUrls: photos,
      };
    }
  }, [property]);

  const formMatchesPlacesLockMemo = useMemo(
    () => formMatchesPlacesLock(address, city, stateProvince, postalCode, placesLock),
    [address, city, stateProvince, postalCode, placesLock]
  );

  const geoPayloadForSave = useMemo(() => {
    if (formMatchesPlacesLockMemo && placesLock) {
      return {
        country: placesLock.country?.trim() || null,
        formatted_address: placesLock.formatted_address?.trim() || null,
        latitude: placesLock.latitude ?? null,
        longitude: placesLock.longitude ?? null,
      };
    }
    return {
      country: null,
      formatted_address: null,
      latitude: null,
      longitude: null,
    };
  }, [formMatchesPlacesLockMemo, placesLock]);

  const handleAddressSelect = (details: AddressDetails) => {
    setAddress((details.street_address || details.formatted_address || "").trim());
    setCity((details.city || "").trim());
    setStateProvince((details.state_province || "").trim());
    setPostalCode((details.postal_code || "").trim());
    setPlacesLock(details);
  };

  const hasUnsavedChanges = (() => {
    const s = initialSnapshotRef.current;
    if (!s) return false;
    if (name !== s.name || address !== s.address || unit !== s.unit || city !== s.city || stateProvince !== s.stateProvince || postalCode !== s.postalCode || notes !== s.notes || hospitablePropertyId !== s.hospitablePropertyId) return true;
    if (photoUrls.length !== s.photoUrls.length) return true;
    return photoUrls.some((url, i) => (s.photoUrls[i] ?? "") !== url);
  })();

  const goToProperty = () => navigate(`/properties/${propertyId}`);

  const handleExitClick = (target: "back" | "cancel") => {
    if (hasUnsavedChanges) {
      setPendingExitTarget(target);
      setExitConfirmOpen(true);
    } else {
      goToProperty();
    }
  };

  const handleExitConfirmSave = async () => {
    setExitConfirmOpen(false);
    try {
      await updateProperty.mutateAsync({
        propertyId: propertyId!,
        name: name.trim(),
        address: address.trim() || null,
        unit: unit.trim() || null,
        city: city.trim() || null,
        state_province: stateProvince.trim() || null,
        postal_code: postalCode.trim() || null,
        ...geoPayloadForSave,
        notes: notes.trim() || null,
        hospitable_property_id: hospitablePropertyId.trim() || null,
        property_photo_url: photoUrls[0] ?? null,
        property_photo_urls: photoUrls.length ? photoUrls : [],
      });
      toast({ title: "Success", description: "Property updated" });
      setPendingExitTarget(null);
      goToProperty();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Failed to update property";
      toast({ title: "Error", description: message, variant: "destructive" });
    }
  };

  const handleExitConfirmDiscard = () => {
    setExitConfirmOpen(false);
    setPendingExitTarget(null);
    goToProperty();
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    if (!files.length || !propertyId) return;

    setUploading(true);
    const added: string[] = [];
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (!file.type.startsWith("image/")) {
          toast({ title: "Error", description: "Please upload image files only", variant: "destructive" });
          continue;
        }
        if (file.size > 5 * 1024 * 1024) {
          toast({ title: "Error", description: "Each image must be less than 5MB", variant: "destructive" });
          continue;
        }
        const fileExt = file.name.split(".").pop() || "jpg";
        const nextIndex = photoUrls.length + added.length;
        const fileName = `${propertyId}/photo_${nextIndex}.${fileExt}`;

        const { error: uploadError } = await supabase.storage
          .from("property-photos")
          .upload(fileName, file, { upsert: true });

        if (uploadError) throw uploadError;

        const { data: urlData } = supabase.storage.from("property-photos").getPublicUrl(fileName);
        const cacheBustedUrl = `${urlData.publicUrl}?t=${Date.now()}`;
        added.push(cacheBustedUrl);
      }
      if (added.length) {
        setPhotoUrls((prev) => [...prev, ...added]);
        toast({ title: "Success", description: `${added.length} photo(s) added` });
      }
    } catch (error: unknown) {
      console.error("Upload error:", error);
      const msg =
        typeof error === "object" && error !== null && "message" in error
          ? String((error as { message: unknown }).message)
          : error instanceof Error
            ? error.message
            : "Failed to upload photo(s). If this continues, ensure the property-photos bucket exists and storage policies are applied.";
      toast({ title: "Error", description: msg, variant: "destructive" });
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removePhotoAtIndex = async (index: number) => {
    const url = photoUrls[index];
    if (!url || !propertyId) return;

    try {
      const path = url.split("/property-photos/")[1]?.split("?")[0];
      if (path) {
        await supabase.storage.from("property-photos").remove([path]);
      }
      setPhotoUrls((prev) => prev.filter((_, i) => i !== index));
      toast({ title: "Success", description: "Photo removed" });
    } catch (error) {
      console.error("Remove error:", error);
      toast({ title: "Error", description: "Failed to remove photo", variant: "destructive" });
    }
  };

  const movePhoto = (index: number, direction: "up" | "down") => {
    const next = [...photoUrls];
    const target = direction === "up" ? index - 1 : index + 1;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setPhotoUrls(next);
  };

  const handleSave = async () => {
    if (!propertyId) return;

    if (!name.trim()) {
      toast({ title: "Error", description: "Property name is required", variant: "destructive" });
      return;
    }

    try {
      await updateProperty.mutateAsync({
        propertyId,
        name: name.trim(),
        address: address.trim() || null,
        unit: unit.trim() || null,
        city: city.trim() || null,
        state_province: stateProvince.trim() || null,
        postal_code: postalCode.trim() || null,
        ...geoPayloadForSave,
        notes: notes.trim() || null,
        hospitable_property_id: hospitablePropertyId.trim() || null,
        property_photo_url: photoUrls[0] ?? null,
        property_photo_urls: photoUrls.length ? photoUrls : [],
      });
      toast({ title: "Success", description: "Property updated" });
      navigate(`/properties/${propertyId}`);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Failed to update property";
      toast({ title: "Error", description: message, variant: "destructive" });
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center p-6" style={{ minHeight: "calc(100vh - 57px)" }}>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center p-6" style={{ minHeight: "calc(100vh - 57px)" }}>
          <Card className="w-full max-w-md">
            <CardContent className="pt-6">
              <p className="text-muted-foreground">Property not found.</p>
              <Button onClick={() => navigate("/owner/properties")} className="mt-4">
                Back to Properties
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-2xl mx-auto p-4 sm:p-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="icon" onClick={() => handleExitClick("back")} aria-label="Back">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold">Edit Property</h1>
            <p className="text-muted-foreground">{property.name}</p>
          </div>
        </div>

        {/* Property Details first */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">Property Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Property Name *</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Beach House"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              <div className="col-span-2 space-y-2">
                <Label htmlFor="address">Address</Label>
                <AddressAutocomplete
                  value={address}
                  onChange={setAddress}
                  onSelect={handleAddressSelect}
                  placeholder="Start typing to search for an address..."
                />
                {formMatchesPlacesLockMemo && placesLock?.formatted_address ? (
                  <p className="text-xs text-muted-foreground">{placesLock.formatted_address}</p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Search with Google Places to set the map pin; cleaners use this for directions and on-site checks.
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="unit">Unit</Label>
                <Input
                  id="unit"
                  value={unit}
                  onChange={(e) => setUnit(e.target.value)}
                  placeholder="Apt 4B"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="Miami"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="state">State/Province</Label>
                <Input
                  id="state"
                  value={stateProvince}
                  onChange={(e) => setStateProvince(e.target.value)}
                  placeholder="FL"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="postal">Postal Code</Label>
              <Input
                id="postal"
                value={postalCode}
                onChange={(e) => setPostalCode(e.target.value)}
                placeholder="33101"
                className="max-w-[200px]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="hospitable_property_id">Hospitable property ID</Label>
              <Input
                id="hospitable_property_id"
                value={hospitablePropertyId}
                onChange={(e) => setHospitablePropertyId(e.target.value)}
                placeholder="UUID from Hospitable (shared across Admin / Guidebook / CohostAI)"
                className="font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground">
                Canonical cross-app identity. Must match the Hospitable UUID used in Admin OPS and Guidebook.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any additional notes about the property..."
                rows={4}
              />
            </div>
          </CardContent>
        </Card>

        {/* Photos section: compact grid with internal scroll */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">Property Photos</CardTitle>
            <CardDescription>First photo is the cover. Reorder with arrows. Add as many as you like.</CardDescription>
          </CardHeader>
          <CardContent>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handlePhotoUpload}
              className="hidden"
            />

            {photoUrls.length > 0 ? (
              <div className="space-y-3">
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 max-h-[280px] overflow-y-auto rounded-lg border bg-muted/20 p-2">
                  {photoUrls.map((url, index) => (
                    <div
                      key={`${url}-${index}`}
                      className="relative group rounded-lg overflow-hidden border bg-muted aspect-square"
                    >
                      <img src={url} alt="" className="h-full w-full object-cover" />
                      <span className="absolute top-1 left-1 rounded bg-black/60 text-white text-xs px-1.5 py-0.5">
                        {index === 0 ? "Cover" : index + 1}
                      </span>
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                        <Button
                          type="button"
                          variant="secondary"
                          size="icon"
                          className="h-8 w-8"
                          disabled={index === 0}
                          onClick={() => movePhoto(index, "up")}
                          aria-label="Move up"
                        >
                          <span className="text-xs font-bold">↑</span>
                        </Button>
                        <Button
                          type="button"
                          variant="secondary"
                          size="icon"
                          className="h-8 w-8"
                          disabled={index === photoUrls.length - 1}
                          onClick={() => movePhoto(index, "down")}
                          aria-label="Move down"
                        >
                          <span className="text-xs font-bold">↓</span>
                        </Button>
                        <Button
                          type="button"
                          variant="destructive"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => removePhotoAtIndex(index)}
                          aria-label="Remove"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {uploading ? "Uploading..." : "Add more photos"}
                </Button>
              </div>
            ) : (
              <div
                onClick={() => fileInputRef.current?.click()}
                className="aspect-video rounded-lg border-2 border-dashed border-muted-foreground/25 flex flex-col items-center justify-center cursor-pointer hover:border-muted-foreground/50 transition-colors"
              >
                <Image className="w-12 h-12 text-muted-foreground mb-2" />
                <p className="text-muted-foreground text-sm">
                  {uploading ? "Uploading..." : "Click to upload photos (multiple allowed)"}
                </p>
                <p className="text-muted-foreground/60 text-xs mt-1">Max 5MB each</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Save / Cancel at bottom */}
        <div className="sticky bottom-0 left-0 right-0 py-4 -mx-6 px-6 bg-background border-t flex flex-wrap items-center justify-end gap-2">
          <Button variant="outline" onClick={() => handleExitClick("cancel")}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={updateProperty.isPending}>
            <Save className="w-4 h-4 mr-2" />
            {updateProperty.isPending ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </div>

      <AlertDialog open={exitConfirmOpen} onOpenChange={setExitConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>You have unsaved changes</AlertDialogTitle>
            <AlertDialogDescription>
              You&apos;ll lose your progress if you leave. Save first or exit anyway?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setExitConfirmOpen(false); setPendingExitTarget(null); }}>Stay</AlertDialogCancel>
            <AlertDialogAction onClick={handleExitConfirmDiscard} className="bg-muted text-muted-foreground hover:bg-muted/80">
              Exit anyway
            </AlertDialogAction>
            <AlertDialogAction onClick={handleExitConfirmSave}>
              Save
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default EditProperty;